import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { NEUTRAL, STATUS_COLORS, resolveTheme } from './designTokens';
import useBookingData from './hooks/useBookingData';
import useAnamnesisTemplate from './hooks/useAnamnesisTemplate';
import BookingClientPickerModal from './BookingClientPickerModal';
import BookingCalendarView from './BookingCalendarView';
import BookingDayBand, { buildDayRows, isoWeek, MONTHS } from './BookingDayBand';
import DarkHeader, { darkPrimary, darkGhost } from './components/primitives/DarkHeader';
import BookingUpcomingView from './BookingUpcomingView';
import BookingScheduleView from './BookingScheduleView';
import BookingServicesView from './BookingServicesView';
import BookingAnamnesisView from './BookingAnamnesisView';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';

import { isCampaignActive } from './clientUtils';

const DAY_NAMES = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];

function generateSlots(schedule, duration) {
  if (!schedule) return [];
  const slots = [];
  const [startH, startM] = schedule.start_time.split(':').map(Number);
  const [endH, endM] = schedule.end_time.split(':').map(Number);
  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;
  const step = duration + schedule.break_duration;

  let current = startMin;
  while (current + duration <= endMin) {
    const slotStart = `${String(Math.floor(current / 60)).padStart(2, '0')}:${String(current % 60).padStart(2, '0')}`;
    const slotEnd = `${String(Math.floor((current + duration) / 60)).padStart(2, '0')}:${String((current + duration) % 60).padStart(2, '0')}`;
    slots.push({ start: slotStart, end: slotEnd });
    current += step;
  }
  return slots;
}

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function Bookings({ therapist, clients, accentColor = KLINIO_DEFAULT_ACCENT, onOpenClient, bookFor = null, onBookForDone }) {
  const theme = resolveTheme(accentColor);
  const [activeView, setActiveView] = useState('calendar');
  const { schedules, bookings, blockedTimes, services, newBookingIds, loading, error, refetch } = useBookingData(therapist.id);
  const {
    template: anamnesisTemplate,
    questions: anamnesisQuestions,
    templateIntro,
    setTemplateIntro,
    loading: anamnesisLoading,
    error: anamnesisError,
    refetch: refetchAnamnesis,
  } = useAnamnesisTemplate(therapist.id);
  const [selectedWeekStart, setSelectedWeekStart] = useState(getMonday(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);

  // Kundväljare
  const [localClients, setLocalClients] = useState(clients);
  const [pendingSlot, setPendingSlot] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [pickerServiceId, setPickerServiceId] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [bandDate, setBandDate] = useState(() => new Date()); // dagen i dagboxen överst i Kommande
  const [bookingSuccessInfo, setBookingSuccessInfo] = useState(null);
  const [pickerClient, setPickerClient] = useState(null); // vald kund i "Boka tid"
  const [conflict, setConflict] = useState(null); // { dateStr, start, alternatives } efter krock (23P01)
  const [blockingDay, setBlockingDay] = useState(false); // inline-formulär "Blockera dagen" i dagläget

  // Anamnes
  const [editingIntro, setEditingIntro] = useState(false);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ question_text: '', question_type: 'text', is_required: false });
  const [editingQuestion, setEditingQuestion] = useState(null);

  const hasAutoSwitched = useRef(false);

  useEffect(() => {
    refetch();
  }, []);

  // Byt automatiskt till Kommande-vyn vid första inladdning om det finns nya bokningar
  useEffect(() => {
    if (!hasAutoSwitched.current && !loading && newBookingIds.size > 0) {
      setActiveView('upcoming');
      hasAutoSwitched.current = true;
    }
  }, [loading, newBookingIds]);

  // === TJÄNSTER ===
  const addService = async ({ title, description, duration, price, currency, is_active, campaign_enabled, campaign_label, campaign_price, campaign_start_date, campaign_end_date }) => {
    const { error } = await supabase.from('services').insert({
      therapist_id: therapist.id,
      title,
      description: description || null,
      duration: Number(duration),
      price: price !== null ? Number(price) : null,
      currency: currency || 'SEK',
      is_active: is_active !== undefined ? is_active : true,
      campaign_enabled: campaign_enabled || false,
      campaign_label: campaign_label || null,
      campaign_price: campaign_price !== null && campaign_price !== undefined ? Number(campaign_price) : null,
      campaign_start_date: campaign_start_date || null,
      campaign_end_date: campaign_end_date || null,
    });

    if (error) showToast('Fel: ' + error.message, 'error');
    else refetch();
  };

  const updateService = async (id, { title, description, duration, price, currency, is_active, campaign_enabled, campaign_label, campaign_price, campaign_start_date, campaign_end_date }) => {
    const { error } = await supabase
      .from('services')
      .update({
        title,
        description: description || null,
        duration: Number(duration),
        price: price !== null && price !== '' ? Number(price) : null,
        currency: currency || 'SEK',
        is_active,
        campaign_enabled: campaign_enabled || false,
        campaign_label: campaign_label || null,
        campaign_price: campaign_price !== null && campaign_price !== undefined && campaign_price !== '' ? Number(campaign_price) : null,
        campaign_start_date: campaign_start_date || null,
        campaign_end_date: campaign_end_date || null,
      })
      .eq('id', id)
      .eq('therapist_id', therapist.id);
    if (error) showToast('Fel: ' + error.message, 'error');
    else refetch();
  };

  const toggleService = async (id, current) => {
    const { error } = await supabase
      .from('services')
      .update({ is_active: !current })
      .eq('id', id)
      .eq('therapist_id', therapist.id);
    if (error) showToast('Fel: ' + error.message, 'error');
    else refetch();
  };

  const deleteService = async (id) => {
    const { error } = await supabase
      .from('services')
      .update({ is_deleted: true })
      .eq('id', id)
      .eq('therapist_id', therapist.id);
    if (error) showToast('Fel: ' + error.message, 'error');
    else refetch();
  };

  // === VECKOSCHEMA ===
  const addScheduleDay = async ({ day_of_week, start_time, end_time, slot_duration, break_duration }) => {
    const existing = schedules.find(s => s.day_of_week === day_of_week);
    if (existing) { showToast(DAY_NAMES[day_of_week] + ' finns redan i schemat.', 'info'); return false; }

    const { error } = await supabase.from('availability_schedules').insert({
      therapist_id: therapist.id,
      day_of_week,
      start_time,
      end_time,
      slot_duration,
      break_duration,
    });

    if (error) { showToast('Fel: ' + error.message, 'error'); return false; }
    refetch();
    return true;
  };

  const toggleScheduleDay = async (scheduleId, currentActive) => {
    await supabase.from('availability_schedules').update({ is_active: !currentActive }).eq('id', scheduleId);
    refetch();
  };

  const deleteScheduleDay = async (scheduleId) => {
    await supabase.from('availability_schedules').delete().eq('id', scheduleId);
    refetch();
  };

  // === ANAMNES ===
  const saveTemplate = async () => {
    if (anamnesisTemplate) {
      await supabase.from('anamnesis_templates')
        .update({ intro_text: templateIntro, updated_at: new Date().toISOString() })
        .eq('id', anamnesisTemplate.id);
    } else {
      await supabase.from('anamnesis_templates')
        .insert({ therapist_id: therapist.id, intro_text: templateIntro })
        .select().single();
      await refetchAnamnesis();
    }
    setEditingIntro(false);
  };

  const addQuestion = async () => {
    if (!newQuestion.question_text.trim()) return;
    let templateId = anamnesisTemplate?.id;
    if (!templateId) {
      const { data } = await supabase.from('anamnesis_templates')
        .insert({ therapist_id: therapist.id, intro_text: templateIntro })
        .select().single();
      templateId = data.id;
    }
    const maxOrder = anamnesisQuestions.length > 0 ? Math.max(...anamnesisQuestions.map(q => q.sort_order)) : -1;
    const { error } = await supabase.from('anamnesis_questions').insert({
      template_id: templateId,
      question_text: newQuestion.question_text.trim(),
      question_type: newQuestion.question_type,
      is_required: newQuestion.is_required,
      sort_order: maxOrder + 1,
    });
    if (error) { showToast('Fel: ' + error.message, 'error'); return; }
    setNewQuestion({ question_text: '', question_type: 'text', is_required: false });
    setAddingQuestion(false);
    refetchAnamnesis();
  };

  const saveQuestion = async (id) => {
    if (!editingQuestion.question_text.trim()) return;
    await supabase.from('anamnesis_questions').update({
      question_text: editingQuestion.question_text.trim(),
      question_type: editingQuestion.question_type,
      is_required: editingQuestion.is_required,
    }).eq('id', id);
    setEditingQuestion(null);
    refetchAnamnesis();
  };

  const deleteQuestion = async (id) => {
    await supabase.from('anamnesis_questions').delete().eq('id', id);
    refetchAnamnesis();
  };

  const moveQuestion = async (id, direction) => {
    const idx = anamnesisQuestions.findIndex(q => q.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= anamnesisQuestions.length) return;
    const current = anamnesisQuestions[idx];
    const swap = anamnesisQuestions[swapIdx];
    await supabase.from('anamnesis_questions').update({ sort_order: swap.sort_order }).eq('id', current.id);
    await supabase.from('anamnesis_questions').update({ sort_order: current.sort_order }).eq('id', swap.id);
    refetchAnamnesis();
  };

  // === DAGVY HJÄLPFUNKTIONER ===
  // Använd lokala datumkomponenter (inte toISOString som ger UTC) för att undvika
  // att datumjämförelser förskjuts vid tidszoner som UTC+1/UTC+2 (t.ex. Sverige).
  const getDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getDayBookings = (dateStr) => bookings.filter(b => b.date === dateStr);

  const isTimeBooked = (dateStr, startTime, endTime) => {
    return bookings.find(b => {
      if (b.date !== dateStr) return false;
      const bStart = b.start_time?.slice(0, 5);
      const bEnd = b.end_time?.slice(0, 5);
      return startTime < bEnd && endTime > bStart;
    });
  };

  const isSlotBlocked = (dateStr, slotStart, slotEnd) => {
    const dayBlocked = blockedTimes.filter(bt => bt.date === dateStr);
    if (dayBlocked.some(bt => bt.all_day)) return true;
    return dayBlocked.some(bt => {
      if (bt.all_day) return true;
      const btStart = bt.start_time?.slice(0, 5);
      const btEnd = bt.end_time?.slice(0, 5);
      return slotStart < btEnd && slotEnd > btStart;
    });
  };

  // === KUNDVÄLJARE ===
  const filteredForBooking = localClients.filter(c => {
    const term = clientSearch.toLowerCase();
    if (!term) return true;
    return c.name.toLowerCase().includes(term) ||
      (c.email && c.email.toLowerCase().includes(term)) ||
      (c.phone && c.phone.includes(term));
  });

  // Kundens senast bokade (aktiva) tjänst – förval i "Boka tid"
  const activeServiceIds = new Set(services.filter(s => s.is_active).map(s => s.id));
  const lastServiceFor = (clientId) => {
    const past = bookings
      .filter(b => b.client_id === clientId && b.service_id && activeServiceIds.has(b.service_id))
      .sort((a, b) => `${b.date} ${b.start_time}`.localeCompare(`${a.date} ${a.start_time}`));
    return past[0]?.service_id || null;
  };
  const selectPickerClient = (client) => {
    if (!client) { setPickerClient(null); return; }
    const lastServiceId = lastServiceFor(client.id);
    setPickerClient({ ...client, lastServiceId });
    setShowNewClientForm(false);
    if (lastServiceId) setPickerServiceId(lastServiceId);
  };

  const openClientPicker = (dateStr, slotStart, slotEnd) => {
    setPendingSlot({ dateStr, slotStart, slotEnd });
    const active = services.filter(s => s.is_active);
    setPickerServiceId(active.length === 1 ? active[0].id : null);
    setConflict(null);
    setPickerClient(null);
    if (bookFor) {
      const lastServiceId = lastServiceFor(bookFor.id);
      setPickerClient({ ...bookFor, lastServiceId });
      if (lastServiceId) setPickerServiceId(lastServiceId);
    }
    setClientSearch('');
    setShowNewClientForm(false);
    setNewClientName('');
    setNewClientEmail('');
    setNewClientPhone('');
    setBookingNotes('');
  };

  const closeClientPicker = () => {
    const booked = !!bookingSuccessInfo;
    setPendingSlot(null);
    setShowNewClientForm(false);
    setBookingNotes('');
    setBookingSuccessInfo(null);
    setPickerClient(null);
    setConflict(null);
    if (booked && bookFor && onBookForDone) onBookForDone();
  };

  // Närmaste lediga tider (max 3, inom 14 dagar) från en dag – förslag efter krock
  const nearbyFree = (fromDateStr, exclude) => {
    const out = [];
    const now = new Date();
    const todayDs = getDateStr(now);
    const nowHM = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const start = new Date(`${fromDateStr}T12:00:00`);
    for (let i = 0; i < 14 && out.length < 3; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      const ds = getDateStr(d);
      if (ds < todayDs) continue;
      for (const r of dayData(d).rows) {
        if (out.length >= 3) break;
        if (r.kind !== 'free') continue;
        if (ds === todayDs && r.start <= nowHM) continue;
        if (ds === exclude.dateStr && r.start === exclude.start) continue;
        out.push({ dateStr: ds, start: r.start, end: r.end });
      }
    }
    return out;
  };
  const pickAlternative = (a) => {
    setPendingSlot({ dateStr: a.dateStr, slotStart: a.start, slotEnd: a.end });
    setConflict(null);
  };

  const confirmBooking = async (client) => {
    setBookingLoading(true);

    // Räkna ut sluttid baserat på vald tjänst, inte slot-längden
    const selectedService = services.find(s => s.id === pickerServiceId);
    const duration = selectedService ? selectedService.duration : defaultDuration;
    const [h, m] = pendingSlot.slotStart.split(':').map(Number);
    const endMin = h * 60 + m + duration;
    const calculatedEnd = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;

    const campaignActive = isCampaignActive(selectedService);
    const { data: newBooking, error } = await supabase.from('bookings').insert({
      therapist_id: therapist.id,
      client_id: client.id,
      date: pendingSlot.dateStr,
      start_time: pendingSlot.slotStart,
      end_time: calculatedEnd,
      service_id: pickerServiceId || null,
      booked_by: 'therapist',
      client_seen: false,
      therapist_seen: true,
      notes: bookingNotes.trim() || null,
      service_title_snapshot: selectedService?.title ?? null,
      price_snapshot: selectedService?.price ?? null,
      currency_snapshot: selectedService?.currency ?? null,
      campaign_active_snapshot: selectedService ? campaignActive : null,
      campaign_price_snapshot: campaignActive ? selectedService.campaign_price : null,
    }).select('id').single();

    if (error) {
      // T-1: 23P01 = tiden överlappar en annan bokning (exclusion constraint), 23505 = dubbelbokning.
      // Händer t.ex. om en kund bokar samma tid medan terapeuten har bokningsrutan öppen.
      if (error.code === '23P01' || error.code === '23505') {
        // T-14: rutan ligger kvar med kund och behandling; terapeuten väljer en närliggande tid.
        setConflict({ dateStr: pendingSlot.dateStr, start: pendingSlot.slotStart, alternatives: nearbyFree(pendingSlot.dateStr, { dateStr: pendingSlot.dateStr, start: pendingSlot.slotStart }) });
        refetch();
      } else {
        showToast('Fel: ' + error.message, 'error');
      }
    } else {
      setBookingSuccessInfo({ clientName: client.name, date: pendingSlot.dateStr, startTime: pendingSlot.slotStart, endTime: calculatedEnd });
      refetch();
      // Skicka bokningsbekräftelse via e-post (tyst i bakgrunden)
      if (newBooking?.id) {
        supabase.functions.invoke('send-booking-confirmation', {
          body: { booking_id: newBooking.id, therapist_id: therapist.id },
        }).catch(err => console.error('[booking-confirm] Fel vid skickande av bokningsbekräftelse:', err));
      }
    }
    setBookingLoading(false);
  };

  const addClientAndBook = async (e) => {
    e.preventDefault();
    if (!newClientName.trim()) return;
    setBookingLoading(true);

    const { data: newClient, error } = await supabase
      .from('clients')
      .insert({
        therapist_id: therapist.id,
        name: newClientName.trim(),
        email: newClientEmail.trim().toLowerCase() || null,
        phone: newClientPhone.trim() || null,
      })
      .select()
      .single();

    if (error) {
      showToast('Fel vid skapande av kund: ' + error.message, 'error');
      setBookingLoading(false);
      return;
    }

    setLocalClients(prev => [...prev, newClient]);
    setPickerClient({ ...newClient, lastServiceId: null });
    setShowNewClientForm(false);
    await confirmBooking(newClient);
  };

  const cancelBooking = async (bookingId) => {
    await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', bookingId);
    refetch();
  };

  const saveNote = async (bookingId, text) => {
    const { error } = await supabase.from('bookings').update({ notes: (text || '').trim() || null }).eq('id', bookingId);
    if (error) { showToast('Kunde inte spara anteckningen.', 'error'); return; }
    showToast('Anteckning sparad', 'success');
    refetch();
  };

  // Dagens rader (bokningar, lediga, blockerade) för en given dag
  const dayData = (date) => {
    const dateStr = getDateStr(date);
    const schedule = schedules.find(s => s.day_of_week === date.getDay() && s.is_active);
    return {
      schedule,
      ...buildDayRows({
        date,
        schedule,
        slots: generateSlots(schedule, defaultDuration),
        dayBookings: getDayBookings(dateStr),
        dayBlocked: blockedTimes.filter(bt => bt.date === dateStr),
        isTimeBooked,
        isSlotBlocked,
      }),
    };
  };

  // Första lediga tid från och med nu bland givna dagar (för "+ Ny bokning" i huvudet)
  const firstFreeSlot = (dates) => {
    const now = new Date();
    const todayStr = getDateStr(now);
    const nowHM = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    for (const date of dates) {
      const ds = getDateStr(date);
      if (ds < todayStr) continue;
      const f = dayData(date).rows.find(r => r.kind === 'free' && (ds > todayStr || r.start > nowHM));
      if (f) return { dateStr: ds, start: f.start, end: f.end };
    }
    return null;
  };

  // Dagens agenda (ljus) under huvudet
  const renderDayBand = (date, extra = {}) => {
    const d = dayData(date);
    return (
      <BookingDayBand
        theme={theme}
        date={date}
        schedule={d.schedule}
        rows={d.rows}
        blockedAll={d.blockedAll}
        blockingDay={!!extra.blockingDay}
        onCancelBlockDay={() => setBlockingDay(false)}
        openClientPicker={openClientPicker}
        planBlock={planBlock}
        blockSeries={blockSeries}
        unblockTime={unblockTime}
        unblockSeries={unblockSeries}
        cancelBooking={cancelBooking}
        saveNote={saveNote}
        onOpenClient={onOpenClient}
        nav={extra.nav}
      />
    );
  };

  // === BLOCKERINGAR (T-15: en gång eller var N:e vecka t.o.m. ett datum) ===
  // spec: { dateStr, start, end } för en tid, { dateStr, allDay: true } för hel dag.
  const planBlock = (spec, repeat, untilStr) => {
    const dates = [];
    const d = new Date(`${spec.dateStr}T12:00:00`);
    const max = new Date(d); max.setDate(max.getDate() + 365);
    const until = repeat > 0 ? new Date(`${untilStr}T12:00:00`) : d;
    while (d <= until && d <= max) {
      dates.push(getDateStr(d));
      if (repeat === 0) break;
      d.setDate(d.getDate() + 7 * repeat);
    }
    const ok = [];
    const conflicts = [];
    dates.forEach(ds => {
      const already = spec.allDay
        ? blockedTimes.some(bt => bt.date === ds && bt.all_day)
        : !!isSlotBlocked(ds, spec.start, spec.end);
      if (already) return;
      // Första tillfället följer tidigare beteende (hel dag kan blockeras trots bokningar);
      // upprepade tillfällen med bokning hoppas över och listas.
      const booked = spec.allDay ? getDayBookings(ds).length > 0 : !!isTimeBooked(ds, spec.start, spec.end);
      if (booked && (repeat > 0 || !spec.allDay)) {
        const x = new Date(`${ds}T12:00:00`);
        const who = spec.allDay ? '' : ` – ${isTimeBooked(ds, spec.start, spec.end)?.client?.name || 'bokad'}`;
        conflicts.push({ dateStr: ds, label: `${DAY_NAMES[x.getDay()].slice(0, 3).toLowerCase()} ${x.getDate()} ${MONTHS[x.getMonth()].slice(0, 3)}${who}` });
        return;
      }
      ok.push(ds);
    });
    return { ok, conflicts };
  };

  const blockSeries = async (spec, { reason, repeat, until }) => {
    const { ok } = planBlock(spec, repeat, until);
    if (ok.length === 0) { showToast('Inga tillfällen att blockera.', 'error'); return; }
    const seriesId = repeat > 0 && ok.length > 1 ? (window.crypto?.randomUUID ? window.crypto.randomUUID() : null) : null;
    const rows = ok.map(ds => ({
      therapist_id: therapist.id,
      date: ds,
      start_time: spec.allDay ? null : spec.start,
      end_time: spec.allDay ? null : spec.end,
      all_day: !!spec.allDay,
      reason,
      series_id: seriesId,
    }));
    const { error } = await supabase.from('blocked_times').insert(rows);
    if (error) { showToast('Fel: ' + error.message, 'error'); return; }
    showToast(rows.length > 1 ? `${rows.length} tillfällen blockerade` : 'Blockerad', 'success');
    refetch();
  };

  // Tar bort seriens kommande tillfällen (idag och framåt); passerade lämnas orörda.
  const unblockSeries = async (seriesId) => {
    const { error } = await supabase.from('blocked_times').delete().eq('series_id', seriesId).gte('date', getDateStr(new Date()));
    if (error) { showToast('Fel: ' + error.message, 'error'); return; }
    showToast('Serien borttagen', 'success');
    refetch();
  };

  const unblockTime = async (blockedId) => {
    await supabase.from('blocked_times').delete().eq('id', blockedId);
    refetch();
  };

  // === VECKOVY ===
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(selectedWeekStart);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const prevWeek = () => { const d = new Date(selectedWeekStart); d.setDate(d.getDate() - 7); setSelectedWeekStart(d); };
  const nextWeek = () => { const d = new Date(selectedWeekStart); d.setDate(d.getDate() + 7); setSelectedWeekStart(d); };
  const thisWeek = () => { setSelectedWeekStart(getMonday(new Date())); };

  const defaultDuration = services.filter(s => s.is_active).length > 0
    ? Math.min(...services.filter(s => s.is_active).map(s => s.duration))
    : 60;

  if (loading) return <p>Laddar...</p>;
  if (error) return <p style={{ color: STATUS_COLORS.error }}>Kunde inte ladda bokningsdata: {error}</p>;

  // === HUVUD (T-16): samma mörka huvud för alla flikar; kalendern har läge vecka/dag ===
  const TABS = [
    { id: 'calendar', label: 'Kalender' },
    { id: 'upcoming', label: 'Kommande' },
    { id: 'schedule', label: 'Veckoschema' },
    { id: 'services', label: 'Tjänster' },
    { id: 'anamnesis', label: 'Anamnes' },
  ];
  const openTab = (id) => {
    setActiveView(id);
    setBlockingDay(false);
    if (id === 'anamnesis') refetchAnamnesis();
  };
  const today = new Date();
  const todayStr = getDateStr(today);
  const weekDates = getWeekDates();
  const weekEndDate = weekDates[6];
  const inCalendar = activeView === 'calendar';
  const dayMode = inCalendar && !!selectedDate;
  const setDay = (d) => { setSelectedDate(d); setBlockingDay(false); };
  const shiftDay = (n) => { const d = new Date(selectedDate); d.setDate(d.getDate() + n); setDay(d); };
  const goWeek = () => { if (selectedDate) setSelectedWeekStart(getMonday(selectedDate)); setDay(null); };
  const goDay = () => {
    const inWeek = weekDates.some(d => getDateStr(d) === todayStr);
    setDay(inWeek ? new Date(today) : new Date(selectedWeekStart));
  };

  const chipBase = { flexShrink: 0, height: '34px', padding: '0 12px', borderRadius: '999px', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const tabChips = (
    <div className="fr-booking-tabs" role="tablist" aria-label="Bokningar" style={{ display: 'flex', gap: '8px', overflowX: 'auto' }}>
      {TABS.map(tab => {
        const on = activeView === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => openTab(tab.id)}
            style={on
              ? { ...chipBase, background: '#FFFFFF', border: '1px solid #FFFFFF', color: NEUTRAL.ink, fontWeight: '600' }
              : { ...chipBase, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.18)', color: '#FFFFFF', fontWeight: '500' }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  const ghost = darkGhost(theme);
  const segBtn = (on) => ({ flex: 1, background: on ? '#FFFFFF' : 'transparent', border: 'none', color: on ? NEUTRAL.ink : '#FFFFFF', padding: '0 12px', height: '32px', borderRadius: '6px', fontSize: '13px', fontWeight: on ? '600' : '500', cursor: 'pointer', fontFamily: 'inherit' });
  const navActions = (
    <div className="fr-bh-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div role="group" aria-label="Visa" style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: '8px', padding: '3px' }}>
        <button type="button" aria-pressed={!dayMode} onClick={goWeek} style={segBtn(!dayMode)}>Vecka</button>
        <button type="button" aria-pressed={dayMode} onClick={goDay} style={segBtn(dayMode)}>Dag</button>
      </div>
      <button type="button" aria-label={dayMode ? 'Föregående dag' : 'Förra veckan'} onClick={() => (dayMode ? shiftDay(-1) : prevWeek())} style={{ ...ghost, width: '38px', height: '38px', padding: 0 }}>←</button>
      <button type="button" onClick={() => (dayMode ? setDay(new Date(today)) : thisWeek())} style={{ ...ghost, height: '38px', padding: '0 12px' }}>Idag</button>
      <button type="button" aria-label={dayMode ? 'Nästa dag' : 'Nästa vecka'} onClick={() => (dayMode ? shiftDay(1) : nextWeek())} style={{ ...ghost, width: '38px', height: '38px', padding: 0 }}>→</button>
    </div>
  );

  let header;
  if (inCalendar && !dayMode) {
    const weekCount = weekDates.reduce((n, d) => n + getDayBookings(getDateStr(d)).length, 0);
    const free = firstFreeSlot(weekDates);
    const sameMonth = selectedWeekStart.getMonth() === weekEndDate.getMonth();
    const range = `${selectedWeekStart.getDate()}${sameMonth ? '' : ' ' + MONTHS[selectedWeekStart.getMonth()]}–${weekEndDate.getDate()} ${MONTHS[weekEndDate.getMonth()]} ${weekEndDate.getFullYear()}`;
    header = (
      <DarkHeader
        theme={theme}
        className="fr-bookings-head"
        label="Bokningar"
        title={`Vecka ${isoWeek(selectedWeekStart)}`}
        subtitle={`${range} · ${weekCount} ${weekCount === 1 ? 'bokad' : 'bokade'}`}
        actions={(
          <>
            {navActions}
            {free && (
              <div className="fr-bh-btns" style={{ display: 'flex', gap: '8px' }}>
                <button type="button" onClick={() => openClientPicker(free.dateStr, free.start, free.end)} style={{ ...darkPrimary(theme), height: '38px', fontWeight: '700' }}>+ Ny bokning</button>
              </div>
            )}
          </>
        )}
      >
        {tabChips}
      </DarkHeader>
    );
  } else if (dayMode) {
    const d = dayData(selectedDate);
    const ds = getDateStr(selectedDate);
    const free = firstFreeSlot([selectedDate]);
    header = (
      <DarkHeader
        theme={theme}
        className="fr-bookings-head"
        label={`${ds === todayStr ? 'Idag · ' : ''}Bokningar · vecka ${isoWeek(selectedDate)}`}
        title={`${DAY_NAMES[selectedDate.getDay()]} ${selectedDate.getDate()} ${MONTHS[selectedDate.getMonth()]}`}
        subtitle={d.summary}
        actions={(
          <>
            {navActions}
            <div className="fr-bh-btns" style={{ display: 'flex', gap: '8px' }}>
              {d.blockedAll ? (
                <button type="button" onClick={() => unblockTime(d.blockedAll.id)} style={{ ...ghost, height: '38px' }}>Avblockera dagen</button>
              ) : (
                <button type="button" aria-expanded={blockingDay} onClick={() => setBlockingDay(v => !v)} style={{ ...ghost, height: '38px' }}>Blockera dagen</button>
              )}
              {free && (
                <button type="button" onClick={() => openClientPicker(free.dateStr, free.start, free.end)} style={{ ...darkPrimary(theme), height: '38px', fontWeight: '700' }}>+ Ny bokning</button>
              )}
            </div>
          </>
        )}
      >
        {tabChips}
      </DarkHeader>
    );
  } else {
    const tab = TABS.find(t => t.id === activeView);
    const upcomingCount = activeView === 'upcoming' ? bookings.filter(b => b.date > todayStr).length : null;
    header = (
      <DarkHeader
        theme={theme}
        className="fr-bookings-head"
        label="Bokningar"
        title={tab ? tab.label : 'Bokningar'}
        subtitle={upcomingCount != null ? `${upcomingCount} ${upcomingCount === 1 ? 'bokning' : 'bokningar'} efter idag` : undefined}
      >
        {tabChips}
      </DarkHeader>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <BookingClientPickerModal
          pendingSlot={pendingSlot}
          bookingSuccessInfo={bookingSuccessInfo}
          closeClientPicker={closeClientPicker}
          services={services}
          pickerServiceId={pickerServiceId}
          setPickerServiceId={setPickerServiceId}
          bookingNotes={bookingNotes}
          setBookingNotes={setBookingNotes}
          clientSearch={clientSearch}
          setClientSearch={setClientSearch}
          filteredForBooking={filteredForBooking}
          bookingLoading={bookingLoading}
          confirmBooking={confirmBooking}
          showNewClientForm={showNewClientForm}
          setShowNewClientForm={setShowNewClientForm}
          newClientName={newClientName}
          setNewClientName={setNewClientName}
          newClientEmail={newClientEmail}
          setNewClientEmail={setNewClientEmail}
          newClientPhone={newClientPhone}
          setNewClientPhone={setNewClientPhone}
          addClientAndBook={addClientAndBook}
          accentColor={accentColor}
          pickerClient={pickerClient}
          selectPickerClient={selectPickerClient}
          conflict={conflict}
          pickAlternative={pickAlternative}
        />

      {header}

      {bookFor && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', background: theme.accent_soft_bg, color: theme.accent_soft_text, borderRadius: '10px', padding: '10px 14px', fontSize: '14px' }}>
          <span style={{ flex: 1, minWidth: 0 }}>Bokar åt <strong>{bookFor.name}</strong> – välj en ledig tid.</span>
          <button type="button" onClick={onBookForDone} style={{ background: theme.card_bg, border: '1px solid #D5DBE4', color: NEUTRAL.ink, padding: '7px 12px', minHeight: '36px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Avbryt</button>
        </div>
      )}

      {inCalendar && !dayMode && (
        <BookingCalendarView
          theme={theme}
          selectedWeekStart={selectedWeekStart}
          getWeekDates={getWeekDates}
          getDateStr={getDateStr}
          getDayBookings={getDayBookings}
          schedules={schedules}
          blockedTimes={blockedTimes}
          isTimeBooked={isTimeBooked}
          isSlotBlocked={isSlotBlocked}
          defaultDuration={defaultDuration}
          setSelectedDate={setDay}
          openClientPicker={openClientPicker}
          onOpenClient={onOpenClient}
          planBlock={planBlock}
          blockSeries={blockSeries}
        />
      )}

      {dayMode && renderDayBand(selectedDate, { blockingDay })}

      {activeView === 'upcoming' && (
        <>
          {renderDayBand(bandDate, {
            nav: {
              title: `${getDateStr(bandDate) === todayStr ? 'Idag · ' : ''}${DAY_NAMES[bandDate.getDay()]} ${bandDate.getDate()} ${MONTHS[bandDate.getMonth()]}`,
              onPrev: () => { const x = new Date(bandDate); x.setDate(x.getDate() - 1); setBandDate(x); },
              onNext: () => { const x = new Date(bandDate); x.setDate(x.getDate() + 1); setBandDate(x); },
            },
          })}
          <BookingUpcomingView
            theme={theme}
            bookings={bookings}
            newBookingIds={newBookingIds}
            getDateStr={getDateStr}
            cancelBooking={cancelBooking}
            saveNote={saveNote}
            onOpenClient={onOpenClient}
            onPickDate={(d) => { setBandDate(d); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          />
        </>
      )}

      {activeView === 'schedule' && (
        <BookingScheduleView
          theme={theme}
          schedules={schedules}
          addScheduleDay={addScheduleDay}
          toggleScheduleDay={toggleScheduleDay}
          deleteScheduleDay={deleteScheduleDay}
        />
      )}

      {activeView === 'services' && (
        <BookingServicesView
          theme={theme}
          services={services}
          addService={addService}
          updateService={updateService}
          toggleService={toggleService}
          deleteService={deleteService}
        />
      )}

      {activeView === 'anamnesis' && anamnesisError && (
        <p style={{ color: STATUS_COLORS.error }}>Kunde inte ladda anamnesdata: {anamnesisError}</p>
      )}
      {activeView === 'anamnesis' && !anamnesisError && (
        <BookingAnamnesisView
          theme={theme}
          anamnesisLoading={anamnesisLoading}
          anamnesisQuestions={anamnesisQuestions}
          templateIntro={templateIntro}
          setTemplateIntro={setTemplateIntro}
          editingIntro={editingIntro}
          setEditingIntro={setEditingIntro}
          saveTemplate={saveTemplate}
          addingQuestion={addingQuestion}
          setAddingQuestion={setAddingQuestion}
          newQuestion={newQuestion}
          setNewQuestion={setNewQuestion}
          editingQuestion={editingQuestion}
          setEditingQuestion={setEditingQuestion}
          addQuestion={addQuestion}
          saveQuestion={saveQuestion}
          deleteQuestion={deleteQuestion}
          moveQuestion={moveQuestion}
        />
      )}
    </div>
  );
}

export default Bookings;
